// Ring Frame Time & Motion — Phase 1 tablet capture screen (Tasks 8-9).
//
// Replaces the paper "UNITECH COTSPIN LTD. HIMATPUR — Time Motion Study"
// sheet. An HR observer follows one ring-frame sider for a full 8-hour
// shift, alone, on their feet — every design decision here serves that:
// large touch targets, minimal taps, nothing to type while walking.
//
// Backend: app/api/routers/live_study.py rf_* endpoints,
// app/services/ring_frame_study_service.py (see its module docstring for
// the fairness rules this screen must not violate — a failed piecing +
// retry is ONE break event, never two; Process/Machine breakage is never
// combined with Worker Response into a single score).
//
// Offline handling: every mutating call carries a client_tap_id and goes
// through the shared apiClient (frontend/src/shared/api/client.ts), which
// already retries transient failures (3x, exponential backoff) and — for
// URLs allow-listed in frontend/src/shared/api/offlineQueue.ts — queues
// the write in IndexedDB if retries are exhausted, replaying it in the
// background. That allow-list deliberately excludes the two calls that
// create a brand-new server id (round/start, round/{id}/mend/start): the
// tablet needs that id immediately to keep capturing (e.g. mend/stop
// needs the event id mend/start returns), and a queued create only
// yields a synthetic `{_queued:true}` marker with no id. Those two calls
// rely on apiClient's retry alone and surface a clear error if that is
// exhausted, rather than being silently swallowed. Every other rf/ call
// here operates on an id already held locally, so the UI applies the
// change optimistically first and reconciles with the server's response
// when it is not a queued marker.
import { useEffect, useMemo, useState } from 'react'
import {
  App, Alert, Badge, Button, Card, Col, Descriptions, Drawer, Empty,
  InputNumber, Row, Select, Space, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CloudSyncOutlined, LeftOutlined, UndoOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import {
  ringFrameStudyApi,
  type RfBreakEvent,
  type RfLossItem,
  type RfResult,
  type RfRound,
  type RfSide,
} from '@/features/hr/ringFrameStudyApi'
import { liveStudyApi } from '@/features/hr/liveStudyApi'
import { countQueue, onQueueChanged } from '@/shared/api/offlineQueue'
import { apiError } from '@/shared/utils/apiError'

const { Text, Title, Paragraph } = Typography

const SIDES: RfSide[] = ['RHS', 'LHS']

type CounterName = 'creel_open' | 'cots_jam'

const LOSS_REASONS = [
  'NO_ROVING', 'NO_MATERIAL', 'WAIT_FITTER', 'WAIT_ELECTRICIAN',
  'BREAKDOWN', 'NO_TROLLEY', 'SUPERVISOR', 'QUALITY_HOLD', 'WORKER', 'UNKNOWN',
] as const
type LossReason = typeof LOSS_REASONS[number]

// Maps each reason to a `loss_owner` the backend actually accepts
// (WORKER, MACHINE, MAINTENANCE, MATERIAL, UTILITY, PLANNING, SUPERVISION,
// UNKNOWN — see work_study_round.py LOSS_OWNERS). WAIT_FITTER/
// WAIT_ELECTRICIAN are "waiting for a maintenance person" -> MAINTENANCE;
// BREAKDOWN is "the machine itself is down" -> MACHINE. QUALITY_HOLD has
// no dedicated QUALITY owner in the accepted set, so it is routed to
// SUPERVISION (a QC hold is released by a supervisory decision) — flagged
// here for the process owner to confirm.
const REASON_TO_OWNER: Record<LossReason, string> = {
  NO_ROVING: 'MATERIAL',
  NO_MATERIAL: 'MATERIAL',
  NO_TROLLEY: 'MATERIAL',
  WAIT_FITTER: 'MAINTENANCE',
  WAIT_ELECTRICIAN: 'MAINTENANCE',
  BREAKDOWN: 'MACHINE',
  SUPERVISOR: 'SUPERVISION',
  QUALITY_HOLD: 'SUPERVISION',
  WORKER: 'WORKER',
  UNKNOWN: 'UNKNOWN',
}

interface SideState {
  activeRound: RfRound | null
  completedRounds: RfRound[]
  openMend: { event: RfBreakEvent; startedAtMs: number } | null
  lastFailedEventId: string | null
  lastEventId: string | null
  lastCounterTapped: CounterName | null
  starting: boolean
  startError: string | null
}

type SidesState = Record<RfSide, SideState>

function emptySide(): SideState {
  return {
    activeRound: null,
    completedRounds: [],
    openMend: null,
    lastFailedEventId: null,
    lastEventId: null,
    lastCounterTapped: null,
    starting: false,
    startError: null,
  }
}

function defaultSides(): SidesState {
  return { RHS: emptySide(), LHS: emptySide() }
}

// ── UI-state persistence (localStorage) ────────────────────────────────
// There is no "list rounds for a session" GET endpoint (Phase 1 scope) —
// rounds/events live only in the response of each mutating call. So a
// page reload would otherwise blank the on-screen tallies even though the
// data is safe on the server. This is a *display* cache only, separate
// from the offline write queue: the source of truth for the result stays
// the server (rf/result / rf/submit recompute from the DB every time).
interface PersistedShape {
  sides: SidesState
  events: RfBreakEvent[]
}

function storageKey(sessionId: string) {
  return `rf_study_ui_${sessionId}`
}

function loadPersisted(sessionId: string): PersistedShape | null {
  try {
    const raw = localStorage.getItem(storageKey(sessionId))
    if (!raw) return null
    return JSON.parse(raw) as PersistedShape
  } catch {
    return null
  }
}

function savePersisted(sessionId: string, sides: SidesState, events: RfBreakEvent[]) {
  try {
    localStorage.setItem(storageKey(sessionId), JSON.stringify({ sides, events }))
  } catch {
    // Quota exceeded / private mode — the offline queue still protects the
    // actual data; only this display cache is lost.
  }
}

// ── Queued-write detection ──────────────────────────────────────────────
// apiClient's response interceptor synthesizes this shape when a write
// gets handed to the offline queue instead of throwing (see client.ts).
interface QueuedMarker { _queued: true; _queueId?: string }
function isQueued(x: unknown): x is QueuedMarker {
  return typeof x === 'object' && x !== null && (x as Record<string, unknown>)._queued === true
}

// ── formatting helpers ──────────────────────────────────────────────────
function fmtMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
}
function fmtNum(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return n.toFixed(digits)
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return `${n.toFixed(1)}%`
}
function newTapId(): string {
  return crypto.randomUUID()
}

interface RfSubmitErrorDetail { errors?: string[]; warnings?: string[] }
function extractSubmitDetail(e: unknown): RfSubmitErrorDetail | null {
  if (!e || typeof e !== 'object' || !('response' in e)) return null
  const resp = (e as { response?: { status?: number; data?: { detail?: unknown } } }).response
  if (resp?.status !== 400) return null
  const detail = resp.data?.detail
  if (detail && typeof detail === 'object') return detail as RfSubmitErrorDetail
  return null
}

export default function RingFrameStudyCapturePage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { message } = App.useApp()

  const [activeTab, setActiveTab] = useState<RfSide>('RHS')

  const [sides, setSides] = useState<SidesState>(() => {
    if (!sessionId) return defaultSides()
    const persisted = loadPersisted(sessionId)
    if (!persisted) return defaultSides()
    const sanitized = { ...persisted.sides }
    for (const side of SIDES) {
      // A "starting" flag frozen mid-flight by a reload would otherwise
      // never clear (no promise survives a reload to resolve it).
      sanitized[side] = { ...sanitized[side], starting: false, startError: null }
    }
    return sanitized
  })
  const [events, setEvents] = useState<RfBreakEvent[]>(() => {
    if (!sessionId) return []
    return loadPersisted(sessionId)?.events ?? []
  })

  useEffect(() => {
    if (!sessionId) return
    savePersisted(sessionId, sides, events)
  }, [sessionId, sides, events])

  // ── Header context ──
  const { data: bundle } = useQuery({
    queryKey: ['study-session', sessionId],
    queryFn: () => liveStudyApi.getSession(sessionId!),
    enabled: !!sessionId,
  })
  const sess = bundle?.session

  const { data: ctx } = useQuery({
    queryKey: ['rf-context', sessionId, sess?.employee_id, sess?.machine_id, sess?.shift_id, sess?.study_date],
    queryFn: () => ringFrameStudyApi.getContext({
      employee_id: sess?.employee_id ?? undefined,
      machine_id: sess?.machine_id ?? undefined,
      shift_id: sess?.shift_id ?? undefined,
      study_date: sess?.study_date,
    }),
    enabled: !!sess,
  })

  // ── Unsynced-tap badge — reuses offlineQueue's own counters directly ──
  const [queueCounts, setQueueCounts] = useState({ pending: 0, failed: 0 })
  useEffect(() => {
    let mounted = true
    const refresh = () => { void countQueue().then((c) => { if (mounted) setQueueCounts(c) }) }
    refresh()
    const unsub = onQueueChanged(refresh)
    return () => { mounted = false; unsub() }
  }, [])
  const unsyncedCount = queueCounts.pending + queueCounts.failed

  // ── Live MEND timer tick ──
  const [nowTick, setNowTick] = useState(() => Date.now())
  useEffect(() => {
    const anyOpen = SIDES.some((s) => sides[s].openMend)
    if (!anyOpen) return
    const id = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [sides])

  // ── Round auto-start ──
  // Idempotent: if a round is already open for this side (persisted state,
  // or a previous call already in flight/succeeded) this is a no-op.
  function ensureRoundStarted(side: RfSide) {
    let alreadyHave = false
    setSides((s) => {
      if (s[side].activeRound || s[side].starting) { alreadyHave = true; return s }
      return { ...s, [side]: { ...s[side], starting: true, startError: null } }
    })
    if (alreadyHave || !sessionId) return
    void (async () => {
      try {
        const tapId = newTapId()
        const round = await ringFrameStudyApi.startRound(sessionId, side, tapId)
        if (isQueued(round)) {
          throw new Error('The tablet is offline — the round could not start yet.')
        }
        setSides((s) => ({ ...s, [side]: { ...s[side], activeRound: round, starting: false } }))
      } catch (e) {
        setSides((s) => ({
          ...s,
          [side]: {
            ...s[side],
            starting: false,
            startError: apiError(e, 'Could not start the round — check connection and try again'),
          },
        }))
      }
    })()
  }

  useEffect(() => {
    if (!sessionId) return
    ensureRoundStarted(activeTab)
    // ensureRoundStarted reads/writes state only via the setSides updater
    // callback, so it needs no other dependency to stay correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sessionId])

  // ── Tap handlers ──────────────────────────────────────────────────────

  function handleTapCounter(side: RfSide, counter: CounterName) {
    const round = sides[side].activeRound
    if (!round || !sessionId) return
    const tapId = newTapId()
    setSides((s) => {
      const r = s[side].activeRound
      if (!r) return s
      return {
        ...s,
        [side]: { ...s[side], lastCounterTapped: counter, activeRound: { ...r, [counter]: r[counter] + 1 } },
      }
    })
    ringFrameStudyApi.tapCounter(sessionId, round.id, counter, tapId)
      .then((data) => {
        if (isQueued(data)) return
        setSides((s) => ({ ...s, [side]: { ...s[side], activeRound: data } }))
      })
      .catch((e) => {
        message.error(apiError(e, 'Tap failed'))
        setSides((s) => {
          const r = s[side].activeRound
          if (!r) return s
          return { ...s, [side]: { ...s[side], activeRound: { ...r, [counter]: Math.max(0, r[counter] - 1) } } }
        })
      })
  }

  function handleUndo(side: RfSide) {
    const round = sides[side].activeRound
    const counter = sides[side].lastCounterTapped
    if (!round || !counter || !sessionId) return
    setSides((s) => {
      const r = s[side].activeRound
      if (!r) return s
      return {
        ...s,
        [side]: { ...s[side], lastCounterTapped: null, activeRound: { ...r, [counter]: Math.max(0, r[counter] - 1) } },
      }
    })
    ringFrameStudyApi.undoCounter(sessionId, round.id, counter, newTapId())
      .then((data) => {
        if (isQueued(data)) return
        setSides((s) => ({ ...s, [side]: { ...s[side], activeRound: data } }))
      })
      .catch((e) => message.error(apiError(e, 'Undo failed')))
  }

  function handleLeftDown(side: RfSide) {
    const round = sides[side].activeRound
    if (!round || !sessionId) return
    const tapId = newTapId()
    const localId = `local-${tapId}`
    // Captured now, at the moment of the tap — sent as-is even if this
    // request is queued offline and replayed minutes later.
    const occurredAt = new Date().toISOString()
    const placeholder: RfBreakEvent = {
      id: localId, round_id: round.id, side, occurred_at: occurredAt,
      attended: false, piecing_attempts: 0, successful: null, is_repeat: false,
      em_seconds: 0, mend_open: false,
    }
    setEvents((e) => [...e, placeholder])
    setSides((s) => ({ ...s, [side]: { ...s[side], lastEventId: localId } }))
    ringFrameStudyApi.leftDown(sessionId, round.id, tapId, occurredAt)
      .then((data) => {
        if (isQueued(data)) return
        setEvents((e) => e.map((ev) => (ev.id === localId ? data : ev)))
        setSides((s) => ({ ...s, [side]: { ...s[side], lastEventId: data.id } }))
      })
      .catch((e) => {
        message.error(apiError(e, 'Left-down tap failed'))
        setEvents((e) => e.filter((ev) => ev.id !== localId))
      })
  }

  function handleMendStart(side: RfSide) {
    const round = sides[side].activeRound
    if (!round || !sessionId) return
    const tapId = newTapId()
    ringFrameStudyApi.startMend(sessionId, round.id, tapId)
      .then((data) => {
        if (isQueued(data)) {
          message.error('Could not start the MEND timer — offline. Please tap MEND again once connected.')
          return
        }
        setEvents((e) => [...e, data])
        setSides((s) => ({
          ...s,
          [side]: {
            ...s[side],
            openMend: { event: data, startedAtMs: Date.now() },
            lastFailedEventId: null,
            lastEventId: data.id,
          },
        }))
      })
      .catch((e) => message.error(apiError(e, 'Could not start MEND — check connection and tap MEND again')))
  }

  function handleMendStop(side: RfSide, successful: boolean) {
    const open = sides[side].openMend
    if (!open || !sessionId) return
    const tapId = newTapId()
    const elapsedSec = Math.round((Date.now() - open.startedAtMs) / 1000)
    const optimisticEvent: RfBreakEvent = {
      ...open.event,
      mend_open: false,
      successful,
      em_seconds: (open.event.em_seconds || 0) + elapsedSec,
    }
    setEvents((e) => e.map((ev) => (ev.id === open.event.id ? optimisticEvent : ev)))
    setSides((s) => ({
      ...s,
      [side]: { ...s[side], openMend: null, lastFailedEventId: successful ? null : open.event.id },
    }))
    ringFrameStudyApi.stopMend(sessionId, open.event.id, successful, tapId, elapsedSec)
      .then((data) => {
        if (isQueued(data)) return
        setEvents((e) => e.map((ev) => (ev.id === data.id ? data : ev)))
      })
      .catch((e) => message.error(apiError(e, 'Could not record the outcome — will need to retry')))
  }

  function handleRetry(side: RfSide) {
    const eventId = sides[side].lastFailedEventId
    if (!eventId || !sessionId) return
    const tapId = newTapId()
    const prior = events.find((ev) => ev.id === eventId)
    if (prior) {
      const reopened: RfBreakEvent = {
        ...prior, mend_open: true, successful: null, piecing_attempts: (prior.piecing_attempts || 0) + 1,
      }
      setEvents((e) => e.map((ev) => (ev.id === eventId ? reopened : ev)))
      setSides((s) => ({
        ...s,
        [side]: { ...s[side], lastFailedEventId: null, openMend: { event: reopened, startedAtMs: Date.now() } },
      }))
    }
    ringFrameStudyApi.retryMend(sessionId, eventId, tapId)
      .then((data) => {
        if (isQueued(data)) return
        setEvents((e) => e.map((ev) => (ev.id === data.id ? data : ev)))
        setSides((s) => ({
          ...s,
          [side]: {
            ...s[side],
            openMend: s[side].openMend ? { event: data, startedAtMs: s[side].openMend!.startedAtMs } : { event: data, startedAtMs: Date.now() },
          },
        }))
      })
      .catch((e) => message.error(apiError(e, 'Retry failed')))
  }

  function handleRepeat(side: RfSide) {
    const eventId = sides[side].openMend?.event.id ?? sides[side].lastEventId
    if (!eventId || !sessionId) return
    const tapId = newTapId()
    setEvents((e) => e.map((ev) => (ev.id === eventId ? { ...ev, is_repeat: true } : ev)))
    ringFrameStudyApi.repeatMend(sessionId, eventId, tapId)
      .then((data) => { if (!isQueued(data)) setEvents((e) => e.map((ev) => (ev.id === data.id ? data : ev))) })
      .catch((e) => message.error(apiError(e, 'Repeat flag failed')))
  }

  async function handleNextRound(side: RfSide) {
    const round = sides[side].activeRound
    if (!round || sides[side].openMend || !sessionId) return
    const tapId = newTapId()
    const nowIso = new Date().toISOString()
    const localDuration = round.started_at
      ? Math.max(0, Math.round((Date.now() - new Date(round.started_at).getTime()) / 1000))
      : null
    const optimisticEnded: RfRound = { ...round, ended_at: nowIso, duration_seconds: localDuration }
    setSides((s) => ({
      ...s,
      [side]: {
        ...s[side], activeRound: null, completedRounds: [...s[side].completedRounds, optimisticEnded],
      },
    }))
    try {
      const endedData = await ringFrameStudyApi.endRound(sessionId, round.id, tapId, localDuration)
      if (!isQueued(endedData)) {
        setSides((s) => ({
          ...s,
          [side]: {
            ...s[side],
            completedRounds: s[side].completedRounds.map((r) => (r.id === round.id ? endedData : r)),
          },
        }))
      }
    } catch (e) {
      message.error(apiError(e, 'Could not end the round'))
      setSides((s) => ({
        ...s,
        [side]: {
          ...s[side], activeRound: round,
          completedRounds: s[side].completedRounds.filter((r) => r.id !== round.id),
        },
      }))
      return
    }
    ensureRoundStarted(side)
  }

  // ── Per-round tallies (derived, since the round dict itself only holds
  // creel/cots — ends & mending time live in the break-event rows) ──────
  const roundsWithTallies = useMemo(() => {
    const bySide: Record<RfSide, Array<RfRound & { endsFound: number; emSeconds: number }>> = { RHS: [], LHS: [] }
    for (const side of SIDES) {
      bySide[side] = sides[side].completedRounds.map((r) => {
        const roundEvents = events.filter((e) => e.round_id === r.id)
        return {
          ...r,
          endsFound: roundEvents.length,
          emSeconds: roundEvents.reduce((sum, e) => sum + (e.attended ? e.em_seconds : 0), 0),
        }
      })
    }
    return bySide
  }, [sides, events])

  function activeRoundTallies(side: RfSide) {
    const round = sides[side].activeRound
    if (!round) return { endsAttended: 0 }
    return { endsAttended: events.filter((e) => e.round_id === round.id && e.attended).length }
  }

  // ── Wrap-up drawer state ──
  const [wrapUpOpen, setWrapUpOpen] = useState(false)
  const [lunchMin, setLunchMin] = useState<number | null>(null)
  const [teaMin, setTeaMin] = useState<number | null>(null)
  const [othersMin, setOthersMin] = useState<number | null>(null)
  const [freeMin, setFreeMin] = useState<number | null>(null)
  const [freeReason, setFreeReason] = useState<LossReason | undefined>(undefined)
  const [savingLosses, setSavingLosses] = useState(false)
  const [creelBobbins, setCreelBobbins] = useState<number | null>(null)
  const [creelMinutes, setCreelMinutes] = useState<number | null>(null)
  const [savingCreeling, setSavingCreeling] = useState(false)

  const freeTimeNeedsReason = (freeMin ?? 0) > 0
  const canSaveLosses = !freeTimeNeedsReason || !!freeReason
  const creelingBalanced = (creelBobbins ?? 0) <= 0 || ((creelBobbins ?? 0) > 0 && (creelMinutes ?? 0) > 0)

  async function handleSaveLosses() {
    if (!sessionId || !canSaveLosses) return
    setSavingLosses(true)
    try {
      const losses: RfLossItem[] = []
      if ((lunchMin ?? 0) > 0) losses.push({ bucket: 'LUNCH', minutes: lunchMin! })
      if ((teaMin ?? 0) > 0) losses.push({ bucket: 'TEA', minutes: teaMin! })
      if ((othersMin ?? 0) > 0) losses.push({ bucket: 'WATER_OTHER', minutes: othersMin! })
      if ((freeMin ?? 0) > 0) {
        losses.push({
          bucket: 'FREE', minutes: freeMin!, reason_code: freeReason,
          loss_owner: freeReason ? REASON_TO_OWNER[freeReason] : undefined,
        })
      }
      await ringFrameStudyApi.saveLosses(sessionId, losses)
      message.success('Shift losses saved')
    } catch (e) {
      message.error(apiError(e, 'Could not save shift losses'))
    } finally {
      setSavingLosses(false)
    }
  }

  async function handleSaveCreeling() {
    if (!sessionId || !creelingBalanced) return
    setSavingCreeling(true)
    try {
      await ringFrameStudyApi.saveCreeling(sessionId, creelBobbins ?? 0, creelMinutes ?? undefined)
      message.success('Creeling saved')
    } catch (e) {
      message.error(apiError(e, 'Could not save creeling'))
    } finally {
      setSavingCreeling(false)
    }
  }

  // ── Result + submit ──
  const [result, setResult] = useState<RfResult | null>(null)
  const [loadingResult, setLoadingResult] = useState(false)
  const [submitErrors, setSubmitErrors] = useState<string[] | null>(null)
  const [submitWarnings, setSubmitWarnings] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  async function handlePreviewResult() {
    if (!sessionId) return
    setLoadingResult(true)
    try {
      const r = await ringFrameStudyApi.getResult(sessionId)
      setResult(r)
    } catch (e) {
      message.error(apiError(e, 'Could not compute result'))
    } finally {
      setLoadingResult(false)
    }
  }

  async function handleSubmit() {
    if (!sessionId) return
    setSubmitting(true)
    setSubmitErrors(null)
    try {
      const out = await ringFrameStudyApi.submitStudy(sessionId)
      setResult(out.result)
      setSubmitWarnings(out.warnings)
      message.success('Study submitted')
    } catch (e) {
      const detail = extractSubmitDetail(e)
      if (detail) {
        setSubmitErrors(detail.errors ?? [])
        setSubmitWarnings(detail.warnings ?? [])
      } else {
        message.error(apiError(e, 'Submit failed'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!sessionId) {
    return <div style={{ padding: 24 }}><Text type="danger">No session id in the URL.</Text></div>
  }

  const columns: ColumnsType<RfRound & { endsFound: number; emSeconds: number }> = [
    { title: 'Round', dataIndex: 'round_no', key: 'round_no', width: 70 },
    { title: 'Ends', dataIndex: 'endsFound', key: 'endsFound', width: 70 },
    {
      title: 'EM Time', dataIndex: 'emSeconds', key: 'emSeconds', width: 90,
      render: (v: number) => fmtMMSS(v),
    },
    { title: 'Creel', dataIndex: 'creel_open', key: 'creel_open', width: 70 },
    { title: 'Cots', dataIndex: 'cots_jam', key: 'cots_jam', width: 70 },
    {
      title: 'Duration', dataIndex: 'duration_seconds', key: 'duration_seconds', width: 90,
      render: (v: number | null) => (v === null ? '—' : fmtMMSS(v)),
    },
  ]

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '8px 16px', background: '#fff',
        borderBottom: '1px solid #eee', position: 'sticky', top: 0, zIndex: 10,
      }}
      >
        <Button type="text" icon={<LeftOutlined />} onClick={() => navigate('/hr/live-studies')}>
          Back
        </Button>
        <Title level={5} style={{ flex: 1, textAlign: 'center', margin: 0 }}>
          Ring Frame Time &amp; Motion
        </Title>
        <Tooltip title={unsyncedCount > 0 ? `${unsyncedCount} tap(s) waiting to sync` : 'All taps synced'}>
          <Badge count={unsyncedCount} color={queueCounts.failed > 0 ? 'red' : 'orange'} showZero={false}>
            <CloudSyncOutlined style={{ fontSize: 20, color: unsyncedCount > 0 ? '#fa8c16' : '#52c41a' }} />
          </Badge>
        </Tooltip>
        <Button type="primary" style={{ marginLeft: 12 }} onClick={() => setWrapUpOpen(true)}>
          Finish Study
        </Button>
      </div>

      {/* Header strip — auto-fetched, read-only */}
      <div style={{ padding: '12px 16px' }}>
        {ctx && !ctx.allocation_found && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message="Worker not allocated to this machine today"
            description="The system found no WorkerAllocation matching this employee, machine and shift for the study date. Capture may continue, but confirm allocation before submitting."
          />
        )}
        <Card size="small">
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="MC No">{ctx?.machine_code ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Count">{ctx?.count_name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Sider">
              {ctx?.employee_name ?? '—'}{ctx?.employee_code ? ` (${ctx.employee_code})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Shift">{ctx?.shift_name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Working Hrs">
              {ctx?.working_minutes != null ? fmtMMSS(ctx.working_minutes * 60) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Running Spindles">{ctx?.running_spindles ?? '—'}</Descriptions.Item>
          </Descriptions>
        </Card>
      </div>

      {/* Side tabs */}
      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as RfSide)}
          items={SIDES.map((side) => ({
            key: side,
            label: side,
            children: renderSidePanel(side),
          }))}
        />
      </div>

      {/* Instruction ribbon — pinned, always visible */}
      <div style={{
        padding: '10px 16px', background: '#fffbe6', borderTop: '1px solid #ffe58f',
        position: 'sticky', bottom: 0, flexShrink: 0,
      }}
      >
        <Text style={{ fontSize: 12 }}>
          Observe normal work. Do not coach. Record the actual reason for waiting. Do not
          classify machine, material or maintenance loss as worker idle. Stop only for safety.
        </Text>
      </div>

      {/* Wrap-up drawer */}
      <Drawer
        title="Finish Study"
        width={520}
        open={wrapUpOpen}
        onClose={() => setWrapUpOpen(false)}
      >
        <Card size="small" title="Shift Losses" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Row gutter={12}>
              <Col span={12}>
                <Text>Lunch (min)</Text>
                <InputNumber style={{ width: '100%' }} min={0} value={lunchMin} onChange={setLunchMin} />
              </Col>
              <Col span={12}>
                <Text>Tea (min)</Text>
                <InputNumber style={{ width: '100%' }} min={0} value={teaMin} onChange={setTeaMin} />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Text>Others / drinking water (min)</Text>
                <InputNumber style={{ width: '100%' }} min={0} value={othersMin} onChange={setOthersMin} />
              </Col>
              <Col span={12}>
                <Text>Free Time (min)</Text>
                <InputNumber style={{ width: '100%' }} min={0} value={freeMin} onChange={setFreeMin} />
              </Col>
            </Row>
            {freeTimeNeedsReason && (
              <div>
                <Text>Reason (required — Free Time &gt; 0)</Text>
                <Select
                  style={{ width: '100%' }}
                  value={freeReason}
                  onChange={setFreeReason}
                  placeholder="Select a reason"
                  options={LOSS_REASONS.map((r) => ({ value: r, label: r }))}
                />
              </div>
            )}
            <Button
              type="primary" block loading={savingLosses}
              disabled={!canSaveLosses}
              onClick={() => void handleSaveLosses()}
            >
              Save Losses
            </Button>
          </Space>
        </Card>

        <Card size="small" title="Creeling" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Row gutter={12}>
              <Col span={12}>
                <Text>Bobbins</Text>
                <InputNumber style={{ width: '100%' }} min={0} value={creelBobbins} onChange={setCreelBobbins} />
              </Col>
              <Col span={12}>
                <Text>Minutes</Text>
                <InputNumber style={{ width: '100%' }} min={0} value={creelMinutes} onChange={setCreelMinutes} />
              </Col>
            </Row>
            {!creelingBalanced && (
              <Text type="danger">Minutes is required when bobbins &gt; 0.</Text>
            )}
            <Button
              block loading={savingCreeling} disabled={!creelingBalanced}
              onClick={() => void handleSaveCreeling()}
            >
              Save Creeling
            </Button>
          </Space>
        </Card>

        <Card size="small" title="Result" style={{ marginBottom: 16 }}>
          <Button onClick={() => void handlePreviewResult()} loading={loadingResult} style={{ marginBottom: 12 }}>
            Preview Result
          </Button>
          <ResultPanel result={result} />
        </Card>

        {submitErrors && submitErrors.length > 0 && (
          <Alert
            type="error" showIcon style={{ marginBottom: 12 }}
            message="Cannot submit — fix the following"
            description={(
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {submitErrors.map((err) => <li key={err}>{err}</li>)}
              </ul>
            )}
          />
        )}
        {submitWarnings.length > 0 && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message="Warnings (non-blocking)"
            description={(
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {submitWarnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          />
        )}
        <Button type="primary" danger block loading={submitting} onClick={() => void handleSubmit()}>
          Submit Study
        </Button>
      </Drawer>
    </div>
  )

  // ── Side panel renderer ────────────────────────────────────────────
  function renderSidePanel(side: RfSide) {
    const st = sides[side]
    const round = st.activeRound
    const tallies = activeRoundTallies(side)
    const mendElapsed = st.openMend ? Math.round((nowTick - st.openMend.startedAtMs) / 1000) : 0

    return (
      <div>
        {st.startError && (
          <Alert
            type="error" showIcon style={{ marginBottom: 12 }}
            message={st.startError}
            action={<Button size="small" onClick={() => ensureRoundStarted(side)}>Retry</Button>}
          />
        )}

        {!round && !st.startError && (
          <Card size="small" style={{ marginBottom: 12, textAlign: 'center' }}>
            <Text type="secondary">{st.starting ? 'Starting round…' : 'Preparing round…'}</Text>
          </Card>
        )}

        {round && (
          <Card
            size="small"
            title={`Round ${round.round_no}`}
            extra={(
              <Space size="middle">
                <Text>Ends: <Text strong>{tallies.endsAttended}</Text></Text>
                <Text>Creel: <Text strong>{round.creel_open}</Text></Text>
                <Text>Cots: <Text strong>{round.cots_jam}</Text></Text>
              </Space>
            )}
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {/* MEND toggle */}
              {st.openMend ? (
                <Row gutter={8}>
                  <Col span={24} style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Tag color="processing" style={{ fontSize: 16, padding: '4px 12px' }}>
                      MENDING… {fmtMMSS(mendElapsed)}
                    </Tag>
                  </Col>
                  <Col span={12}>
                    <Button
                      block type="primary" style={{ minHeight: 72, height: 72, fontSize: 20, fontWeight: 700, background: '#52c41a', borderColor: '#52c41a' }}
                      onClick={() => handleMendStop(side, true)}
                    >
                      DONE ✓
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block danger type="primary" style={{ minHeight: 72, height: 72, fontSize: 20, fontWeight: 700 }}
                      onClick={() => handleMendStop(side, false)}
                    >
                      FAILED ✗
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Row gutter={8}>
                  <Col span={st.lastFailedEventId ? 12 : 24}>
                    <Button
                      block type="primary" style={{ minHeight: 72, height: 72, fontSize: 20, fontWeight: 700 }}
                      onClick={() => handleMendStart(side)}
                    >
                      MEND
                    </Button>
                  </Col>
                  {st.lastFailedEventId && (
                    <Col span={12}>
                      <Button
                        block style={{
                          minHeight: 72, height: 72, fontSize: 18, fontWeight: 700,
                          background: '#fff7e6', borderColor: '#fa8c16', color: '#fa8c16', borderStyle: 'dashed', borderWidth: 2,
                        }}
                        onClick={() => handleRetry(side)}
                      >
                        RETRY SAME END
                      </Button>
                    </Col>
                  )}
                </Row>
              )}

              <Row gutter={8}>
                <Col span={12}>
                  <Button block style={{ minHeight: 72, height: 72, fontSize: 16, fontWeight: 600 }} onClick={() => handleLeftDown(side)}>
                    LEFT DOWN +1
                  </Button>
                </Col>
                <Col span={12}>
                  <Button block style={{ minHeight: 72, height: 72, fontSize: 16, fontWeight: 600 }} onClick={() => handleRepeat(side)}>
                    REPEAT
                  </Button>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Button block style={{ minHeight: 72, height: 72, fontSize: 16, fontWeight: 600 }} onClick={() => handleTapCounter(side, 'creel_open')}>
                    CREEL OPEN +1
                  </Button>
                </Col>
                <Col span={12}>
                  <Button block style={{ minHeight: 72, height: 72, fontSize: 16, fontWeight: 600 }} onClick={() => handleTapCounter(side, 'cots_jam')}>
                    COTS JAM +1
                  </Button>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={16}>
                  <Tooltip title={st.openMend ? 'Stop the MEND timer first' : undefined}>
                    <span style={{ display: 'block' }}>
                      <Button
                        block style={{ minHeight: 72, height: 72, fontSize: 18, fontWeight: 700 }}
                        disabled={!!st.openMend}
                        onClick={() => void handleNextRound(side)}
                      >
                        NEXT ROUND
                      </Button>
                    </span>
                  </Tooltip>
                </Col>
                <Col span={8}>
                  <Button
                    block icon={<UndoOutlined />} style={{ minHeight: 72, height: 72 }}
                    disabled={!st.lastCounterTapped}
                    onClick={() => handleUndo(side)}
                  >
                    Undo
                  </Button>
                </Col>
              </Row>
            </Space>
          </Card>
        )}

        <Card size="small" title="Completed Rounds">
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            columns={columns}
            dataSource={roundsWithTallies[side]}
            locale={{ emptyText: <Empty description="No rounds completed yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </Card>
      </div>
    )
  }
}

// ── Result panel — Process/Machine Breakage and Worker Response are two
// CLEARLY SEPARATE sections, never combined into one score (fairness
// rule — a worker on a badly-set frame shows high breakage while
// responding excellently). See ring_frame_study_service.compute_result.
function ResultPanel({ result }: { result: RfResult | null }) {
  if (!result) {
    return <Text type="secondary">Tap "Preview Result" to compute from the rounds captured so far.</Text>
  }
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div>
        <Title level={5} style={{ marginBottom: 4 }}>Process / Machine Breakage</Title>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          How often ends break — driven by machine setting, material and humidity.
        </Paragraph>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Breaks / 1000 spindle-hrs">{fmtNum(result.breakage_per_1000_sh)}</Descriptions.Item>
          <Descriptions.Item label="Breaks / 100 spindle-hrs">{fmtNum(result.breakage_per_100_sh)}</Descriptions.Item>
          <Descriptions.Item label="Total end breaks">{result.total_end_breaks}</Descriptions.Item>
          <Descriptions.Item label="Sides studied">
            {result.sides_studied.length ? result.sides_studied.join(' + ') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Observed spindle-hours (rate denominator)">
            {fmtNum(result.observed_spindle_hours, 2)}
          </Descriptions.Item>
          <Descriptions.Item label="Whole-frame spindle-hours">{fmtNum(result.running_spindle_hours, 2)}</Descriptions.Item>
          <Descriptions.Item label="Ends Down % (separate measure)" span={2}>
            {fmtPct(result.ends_down_pct)}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div>
        <Title level={5} style={{ marginBottom: 4 }}>Worker Response to Breakage</Title>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          How well the sider responded — this is the part within the worker's control.
        </Paragraph>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Break attendance %">{fmtPct(result.break_attendance_pct)}</Descriptions.Item>
          <Descriptions.Item label="Successful piecing %">{fmtPct(result.successful_piecing_pct)}</Descriptions.Item>
          <Descriptions.Item label="Repeat break %">{fmtPct(result.repeat_break_pct)}</Descriptions.Item>
          <Descriptions.Item label="Avg piecing time">
            {result.avg_piecing_sec != null ? fmtMMSS(result.avg_piecing_sec) : '—'}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div>
        <Title level={5} style={{ marginBottom: 4 }}>Study Summary</Title>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Total (A) RHS ends">{result.total_A_ends}</Descriptions.Item>
          <Descriptions.Item label="Total (B) LHS ends">{result.total_B_ends}</Descriptions.Item>
          <Descriptions.Item label="Creel opens">{result.total_creel_open}</Descriptions.Item>
          <Descriptions.Item label="Cots jams">{result.total_cots_jam}</Descriptions.Item>
          <Descriptions.Item label="Rounds RHS / LHS">{result.rounds_rhs} / {result.rounds_lhs}</Descriptions.Item>
          <Descriptions.Item label="Patrol cycle avg/min/max (s)">
            {fmtNum(result.patrol_cycle_sec.avg, 0)} / {fmtNum(result.patrol_cycle_sec.min, 0)} / {fmtNum(result.patrol_cycle_sec.max, 0)}
          </Descriptions.Item>
          <Descriptions.Item label="Losses by bucket" span={2}>
            {Object.keys(result.losses_by_bucket).length === 0 ? '—' : Object.entries(result.losses_by_bucket)
              .map(([k, v]) => `${k}: ${fmtNum(v, 0)}m`).join(' · ')}
          </Descriptions.Item>
          <Descriptions.Item label="Free Time by owner" span={2}>
            {Object.keys(result.free_time_by_owner).length === 0 ? '—' : Object.entries(result.free_time_by_owner)
              .map(([k, v]) => `${k}: ${fmtNum(v, 0)}m`).join(' · ')}
          </Descriptions.Item>
          <Descriptions.Item label="Creeling">
            {result.creeling.bobbins ? `${result.creeling.bobbins} bobbins / ${fmtNum(result.creeling.sec_per_bobbin, 1)}s each` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Breakage: study-measured vs production-log typed">
            {result.typed_breakage_per_1000_sh != null
              ? `${fmtNum(result.breakage_per_1000_sh)} vs ${fmtNum(result.typed_breakage_per_1000_sh)} (gap ${fmtNum(result.breakage_gap_vs_typed)})`
              : 'no typed value logged'}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Space>
  )
}
